import Image from "next/image";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Breadcumb from "@/components/Breadcumb";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import classes from "./Top.module.css";
import whatsappImg from "@/assets/whatsapp-button.png";
import warrantyImg from "@/assets/warranty-sticker.png";

import onlineOnly from "@/assets/svgs/online-only.svg";
import newArrival from "@/assets/svgs/new-arrival.svg";

import CheckBox from "@/components/CheckBox";
import GiftSelect from "@/components/GiftSelect";
import { NotificationOutlineIcon, PlusOutlineIcon } from "@/components/Icons";
import Loader from "@/components/Loader";
import PopupManager from "@/components/PopupManager";
import { CardPriceTag } from "@/components/ProductCard";
import QuantityChange from "@/components/QuantityChange";
import ArrowBoxButton from "@/components/buttons/ArrowBoxButton";
import Button from "@/components/buttons/Button";
import ShareButton from "@/components/buttons/ShareButton";
import useFetch from "@/hooks/useFetch";
import { getGiftListByIds, getProductGifts } from "@/requests/offer";
import { createModeratorRequest } from "@/requests/moderator";
import { getFilterAds } from "@/requests/ads";
import { postNotifyMe } from "@/requests/product";
import { setGlobalPopupElement } from "@/store/features/globalSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  convertNameChar,
  decodeToken,
  getImageArray,
  jsonParseSafe,
  numberWithCommas,
  objectKeysTS,
  toastMe,
} from "@/utils";
import { productImageId, routes } from "@/utils/constants";
import { getS3ImageUrl, getPageRoute } from "@/utils/routes";
import Link from "next/link";
import EMIOptions from "../EMIOptions";
import NotifyMe from "../NotifyMe";
import OfferLogicsForm from "../OfferLogicsForm";
import UpdateSkuModal from "../UpdateSkuModal";
import RatingStars from "@/components/RatingStars";
import { formatImagePath } from "@/utils/split";
import {
  upsertCompareProduct,
  readCompareProducts,
  MAX_COMPARE_PRODUCTS,
} from "@/utils/compare";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";

interface OfferSelect {
  type: OfferLogicType;
  label: string;
}

const offerSelectItems: OfferSelect[] = [
  { type: "EMI", label: "EMI" },
  { type: "GNG", label: "G&G Gift" },
  { type: "BRAND", label: "Brand Gift" },
];

interface OfferSelection {
  type: OfferLogicType;
  active?: boolean;
  disabled?: boolean;
}

interface AttributeType extends ProductDetailsAttribute {
  options: string[];
  types: ("image" | "code" | "text")[];
  visuals: string[];
  skuImages: (string | null)[];
}

interface Props {
  className?: string;
  style?: CSSProperties;
  publiclyAvailable?: boolean;

  product: ProductDetails;
  onAddToCart?: (
    sku: ProductDetailsSkus,
    quantity: number,
    extraData: ExtraAddToCartData,
  ) => void;
  onBuyNow?: (
    sku: ProductDetailsSkus,
    quantity: number,
    extraData: ExtraAddToCartData,
  ) => void;
}

const Top = ({
  className,
  style,
  publiclyAvailable = true,

  product,
  onAddToCart = () => { },
  onBuyNow = () => { },
}: Props) => {
  const dispatch = useAppDispatch();
  const pcRef = useRef(null);

  const cartLoading = useAppSelector((state) => state.cart.isLoading);
  const cartItems = useAppSelector((state) => state.cart.items);
  const name = useAppSelector((state) => state.auth.user?.firstName);
  const accessToken = useAppSelector((state) => state.auth.access_token);
  const showAdminActions = useMemo(() => {
    if (!accessToken) return false;
    const decoded = decodeToken(accessToken) as Record<string, unknown> | null;
    return decoded?.type === "admin" && decoded?.is_moderator === true;
  }, [accessToken]);

  const [showSkuModal, setShowSkuModal] = useState(false);

  const [containerHeight, setContainerHeight] = useState(0);
  const [stockStatus, setStockStatus] = useState<string>(
    product.stockStatus || "",
  );

  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const [showZoom, setShowZoom] = useState(false);

  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
  const [productUrl, setProductUrl] = useState<string>("");

  // ── Compare state ──
  const router = useRouter();
  const [compareAddSuccessOpen, setCompareAddSuccessOpen] = useState(false);
  const [compareAddedProductName, setCompareAddedProductName] = useState<
    string | null
  >(null);
  const compareSuccessTimerRef = useRef<number | null>(null);

  // ── Gift form modal state (admin) ──
  const [showGiftForm, setShowGiftForm] = useState(false);
  const [giftFormLoading, setGiftFormLoading] = useState(false);
  const [giftSaving, setGiftSaving] = useState(false);
  const [showWarrantyPolicy, setShowWarrantyPolicy] = useState(false);
  const [offerLogicsState, setOfferLogicsState] = useState<OfferLogicState>(
    () => ({
      EMI: {
        type: "EMI",
        selected: false,
        mandatory: false,
        forbiddens: [],
        mandatories: [],
      },
      GNG: {
        type: "GNG",
        selected: false,
        mandatory: false,
        forbiddens: [],
        mandatories: [],
        gifts: [],
      },
      BRAND: {
        type: "BRAND",
        selected: false,
        mandatory: false,
        forbiddens: [],
        mandatories: [],
        gifts: [],
      },
      DISCOUNT: {
        type: "DISCOUNT",
        selected: false,
        mandatory: false,
        forbiddens: [],
        mandatories: [],
        discount: 0,
      },
    }),
  );

  useEffect(() => {
    return () => {
      if (compareSuccessTimerRef.current) {
        window.clearTimeout(compareSuccessTimerRef.current);
        compareSuccessTimerRef.current = null;
      }
    };
  }, []);

  // ── Admin: Submit gift update for moderation (ProductGift entity) ──
  const handleSaveGift = async () => {
    setGiftSaving(true);
    try {
      const res = await createModeratorRequest({
        entity_type: "ProductGift",
        entity_id: product.id,
        action_type: "update",
        requested_updated_data: offerLogicsState,
      });

      if (res.Success) {
        toastMe(res.Message || "Gift update request submitted for moderation");
        setShowGiftForm(false);
      } else {
        toastMe(res.Message || "Failed to submit gift update request", "error");
      }
    } catch (err) {
      console.error("Error submitting gift update request:", err);
      toastMe(err, "error");
    } finally {
      setGiftSaving(false);
    }
  };

  // console.log("product details page offer logic", { product });
  const breadcumb = useMemo(() => {
    return [
      {
        name: "Home",
        link: "/",
      },
    ].concat(
      product.categories
        .map((category) => ({
          name: category.name,
          link: `/category/${category.slug}`,
        }))
        .concat([
          {
            name: convertNameChar(product.name),
            link: `/product/${product.slug}`,
          },
        ]),
    );
  }, [product.categories, product.name, product.slug]);

  const skus = useMemo(() => {
    const _skus = [...product.skus];
    return product.skus;
  }, [product.skus]);

  const attributes = useMemo(() => {
    const _attributes: AttributeType[] = [];
    for (const sku of skus) {
      const skuImages =
        typeof sku.images === "string"
          ? sku.images.split(",").filter(Boolean)
          : sku.images || [];
      const skuImage = skuImages?.[0] || null;

      for (const attr of sku?.attributes || []) {
        const index = _attributes.findIndex((a) => a.key === attr.key);
        const visualValue = attr.image || attr.code || attr.value;
        const visualType: "image" | "code" | "text" = attr.image
          ? "image"
          : attr.code
            ? "code"
            : "text";

        if (index === -1) {
          _attributes.push({
            key: attr.key,
            id: attr.id,
            skuId: attr.skuId,
            createdAt: attr.createdAt,
            updatedAt: attr.updatedAt,
            value: attr.value,
            options: [attr.value],
            types: [visualType],
            visuals: [visualValue],
            skuImages: attr.key.toLowerCase() === "color" ? [skuImage] : [],
          });
        } else if (!_attributes[index]?.options?.includes(attr.value)) {
          _attributes[index].options.push(attr.value);
          _attributes[index].types.push(visualType);
          _attributes[index].visuals.push(visualValue);

          if (
            attr.key.toLowerCase() === "color" &&
            _attributes[index].skuImages
          ) {
            _attributes[index].skuImages.push(skuImage);
          }
        }
      }
    }
    return _attributes;
  }, [skus]);

  const selectedSku = useMemo(() => {
    if (selectedAttributes?.length !== attributes?.length) return undefined;

    const sku = skus.find((sku) => {
      for (let i = 0; i < selectedAttributes?.length; i++) {
        if (sku.attributes[i]?.value !== selectedAttributes[i]) return false;
      }
      return true;
    });

    return sku;
  }, [selectedAttributes, skus, attributes]);

  const invalidAttributeOptions: number[][] = useMemo(() => {
    const _invalidAttributeOptions: number[][] = []; // index of invalid options for each attribute

    if (selectedAttributes?.length > 0) {
      // if any attribute is selected
      const options: string[] = [];
      for (let i = 0; i < attributes?.length; i++) {
        // loop through all attributes
        if (i > 0 && selectedAttributes[i - 1] === undefined) break; // if previous attribute is not selected, then break
        for (let j = 0; j < attributes[i]?.options?.length || 0; j++) {
          // loop through all options of current attribute
          // skip if already selected
          // if (selectedAttributes[i] === attributes[i]?.options[j]) {
          // 	continue;
          // }

          const _options = [...options, attributes[i]?.options[j]]; // previous selected options + current option

          // find sku with selected options
          const sku = skus.find((sku) => {
            // find sku with selected options
            for (let k = 0; k < _options?.length; k++) {
              // check if all options match
              if (sku.attributes[k]?.value !== _options[k]) {
                // if any option does not match, then this sku is not valid
                return false;
              }
            }
            return true;
          });

          // if no sku found, then this option is invalid
          if (!sku) {
            if (!_invalidAttributeOptions[i]) {
              // if invalidAttributeOptions[i] is not defined, then define it
              _invalidAttributeOptions[i] = []; // create empty array
            }

            _invalidAttributeOptions[i].push(j); // push invalid option index to invalidAttributeOptions
          }
        }

        // push selected option to options array
        if (selectedAttributes[i]) {
          options.push(selectedAttributes[i]);
        }
      }
    }

    return _invalidAttributeOptions;
  }, [attributes, selectedAttributes, skus]);

  const images: string[] = useMemo(() => {
    const _images: string[] = getImageArray(product.thumbnail);
    for (const sku of skus) {
      _images.push(
        ...(sku.images?.split(",") || []).filter((i) => i.trim() !== ""),
      );
    }

    if (selectedAttributes?.length > 0) {
      const sku = skus.find((sku) => {
        for (let i = 0; i < selectedAttributes?.length; i++) {
          if (
            !!sku.images?.trim() &&
            sku.attributes[i]?.value !== selectedAttributes[i]
          ) {
            return false;
          }
        }
        return true;
      });

      if (sku) {
        const skuImages = sku.images?.split(",") || [];

        return Array.from(new Set([...skuImages, ..._images])).filter(
          (i) => !!i?.trim(),
        );
      }
    }
    return _images;
  }, [skus, product.thumbnail, selectedAttributes]);

  const outOfStock = useMemo(() => {
    const outOfStockAllSkus = skus.every((sk) => sk.stockQuantity === 0);

    if (product.stockStatus === "Stock Out") {
      return true;
    } else if (outOfStockAllSkus) {
      return true;
    }

    if (!selectedSku) return false;

    return selectedSku?.stockQuantity <= 0 || selectedSku?.stockOut;
  }, [selectedSku, product.stockStatus]);

  const comingSoon = useMemo(() => {
    if (!selectedSku && product.stockStatus === "Coming Soon") return true;

    if (selectedSku && selectedSku.comingSoon) return true;

    return false;
  }, [selectedSku, product.stockStatus]);

  const preOrder = useMemo(() => {
    if (!selectedSku) return false;

    return selectedSku?.preOrder;
  }, [selectedSku]);

  const hasPreOrder = useMemo(() => {
    // return skus.find((sku) => !!sku.preOrder);
    return product.stockStatus === "Pre Order";
  }, [skus]);

  const existOnCart = useMemo(() => {
    if (!selectedSku) return false;

    const index = Array.isArray(cartItems)
      ? cartItems.findIndex((item) => item.sku?.sku === selectedSku.sku)
      : -1;

    return index !== -1;
  }, [cartItems, selectedSku]);

  const offerLogics: OfferLogicState = useMemo(() => {
    if (product.gift && typeof jsonParseSafe(product.gift) === "object") {
      return jsonParseSafe(product.gift);
    }

    return {};
  }, [product.gift]);

  const productAds = useFetch(
    () =>
      getFilterAds({
        product_slug: product.slug || "",
      }),
    {
      deps: [product.slug],
    },
  );

  const gngGift = useFetch(
    () => getGiftListByIds(offerLogics["GNG"]?.gifts?.map((g) => g.id) || []),
    {},
  );

  const brandGift = useFetch(
    () => getGiftListByIds(offerLogics["BRAND"]?.gifts?.map((g) => g.id) || []),
    {},
  );

  const gngGiftItems = useMemo(
    () => offerLogics["GNG"]?.gifts || [],
    [offerLogics],
  );

  const brandGiftItems = useMemo(
    () => offerLogics["BRAND"]?.gifts || [],
    [offerLogics],
  );

  const [showEmiPlans, setShowEmiPlans] = useState(false);
  const [showGiftSelect, setShowGiftSelect] = useState<OfferLogicType>();
  const [offerSelection, setOfferSelection] = useState<OfferSelection[]>([
    {
      type: "EMI",
      active: offerLogics?.EMI?.mandatory || false,
      disabled: offerLogics?.EMI?.mandatory || false,
    },
    {
      type: "GNG",
      active: offerLogics?.GNG?.mandatory || false,
      disabled: offerLogics?.GNG?.mandatory || false,
    },
    {
      type: "BRAND",
      active: offerLogics?.BRAND?.mandatory || false,
      disabled: offerLogics?.BRAND?.mandatory || false,
    },
  ]);

  const [selectedGiftIndexes, setSelectedGiftIndexes] = useState<number[]>([]);
  const [selectedBrandGiftIndexes, setSelectedBrandGiftIndexes] = useState<
    number[]
  >([]);

  //if g&g and brand size gift is not selected then gift item automatically unchecked
  useEffect(() => {
    if (!offerSelection[1].active) {
      setSelectedGiftIndexes([]);
    }
    if (!offerSelection[2].active) {
      setSelectedBrandGiftIndexes([]);
    }
  }, [offerSelection]);

  const [showVariationError, setShowVariationError] = useState(false);

  const [discountTimeLeft, setDiscountTimeLeft] = useState<{
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
  } | null>(null);
  const [discountActive, setDiscountActive] = useState(false);

  const selectedGngGifts = useMemo(() => {
    if (!gngGift.response) return [];

    const giftItems: GiftItem[] = [];

    for (const i of selectedGiftIndexes) {
      const gift: GiftItem | undefined = gngGift.response[i];
      const giftItem = gngGiftItems.find((g) => g.id === gift?.id);

      giftItems.push({
        ...gift,
        qty: giftItem?.quantity + "",
        ...(gift?.type !== "cashback" && giftItem?.amount
          ? { plusAmount: Number(giftItem.amount) }
          : {}),
      });
    }
    return giftItems;
  }, [gngGift.response, selectedGiftIndexes, gngGiftItems]);

  const selectedBrandGifts = useMemo(() => {
    if (!brandGift.response) return [];

    const giftItems: GiftItem[] = [];

    for (const i of selectedBrandGiftIndexes) {
      const gift: GiftItem | undefined = brandGift.response[i];
      const giftItem = brandGiftItems.find((g) => g.id === gift?.id);

      giftItems.push({
        ...gift,
        qty: giftItem?.quantity + "",
        ...(gift?.type !== "cashback" && giftItem?.amount
          ? { plusAmount: Number(giftItem.amount) }
          : {}),
      });
    }
    return giftItems;
  }, [brandGift.response, selectedBrandGiftIndexes, brandGiftItems]);

  const totalGiftAmount = useMemo(() => {
    let amount = 0;

    for (const i of selectedGiftIndexes) {
      const gift = gngGift.response?.[i];

      if (gift?.type === "cashback") {
        amount -= Number(gift.amount || 0);
      } else {
        const giftItem = gngGiftItems.find((g) => g.id === gift?.id);
        if (giftItem?.amount) {
          amount += giftItem.amount;
        }
      }
    }

    for (const i of selectedBrandGiftIndexes) {
      const gift = brandGift.response?.[i];

      if (gift?.type === "cashback") {
        amount -= Number(gift.amount || 0);
      } else {
        const giftItem = brandGiftItems.find((g) => g.id === gift?.id);
        if (giftItem?.amount) {
          amount += giftItem.amount;
        }
      }
    }

    return amount;
  }, [
    selectedGiftIndexes,
    selectedBrandGiftIndexes,
    gngGift.response,
    brandGift.response,
    gngGiftItems,
    brandGiftItems,
  ]);

  const cashbackAmount = useMemo(() => {
    let amount = 0;

    for (const i of selectedGiftIndexes) {
      const gift = gngGift.response?.[i];
      if (gift?.type === "cashback") {
        amount += Number(gift.amount || 0);
      }
    }

    for (const i of selectedBrandGiftIndexes) {
      const gift = brandGift.response?.[i];
      if (gift?.type === "cashback") {
        amount += Number(gift.amount || 0);
      }
    }

    return amount;
  }, [
    selectedGiftIndexes,
    selectedBrandGiftIndexes,
    gngGift.response,
    brandGift.response,
  ]);

  const hasGngGifts = useMemo(() => {
    return (
      !!offerLogics["GNG"]?.gifts?.length &&
      (offerLogics["GNG"]?.selected || offerLogics["GNG"]?.mandatory)
    );
  }, [offerLogics]);

  const hasBrandGifts = useMemo(() => {
    return (
      !!offerLogics["BRAND"]?.gifts?.length &&
      (offerLogics["BRAND"]?.selected || offerLogics["BRAND"]?.mandatory)
    );
  }, [offerLogics]);

  const emiActive = useMemo(() => !!offerLogics?.EMI?.selected, [offerLogics]);

  const emiSelected = useMemo(() => {
    return offerSelection.find((s) => s.type === "EMI")?.active;
  }, [offerSelection]);

  const emiType = useMemo(() => {
    return offerLogics?.EMI?.emiTypes?.[0] || "on-demand";
  }, [offerLogics]);

  const emiTenors = useMemo(() => {
    const emi = offerLogics?.EMI;
    const emiTypes = emi?.emiTypes || [];
    const normalizeTenor = (plan: EMIPlan): EMITenor[] => {
      const month = Number(plan.month ?? plan.months ?? plan.tenor);
      const percent = Number(
        plan.percent ?? plan.charged ?? plan.charge ?? plan.interestRate ?? 0,
      );

      return Number.isFinite(month) &&
        month > 0 &&
        Number.isFinite(percent)
        ? [
            {
              name: `${month} Months`,
              percent,
              tenor: month,
              planId: plan.planId ?? plan.id,
            },
          ]
        : [];
    };

    if (emiTypes.includes("online")) {
      return (emi?.emiTenors || []).map((tenor) => ({
        ...tenor,
        percent: 0,
      }));
    }

    if (emiTypes.includes("custom") || emiTypes.includes("global")) {
      const bankPlans =
        emi?.emiBankId == null
          ? undefined
          : emi.emiBankPlans?.[String(emi.emiBankId)];
      const selectedBankPlans = emi?.banks
        ?.filter(
          (bank) => emi.emiBankId == null || bank.bankId === emi.emiBankId,
        )
        .flatMap((bank) => bank.tenors);
      const plans =
        bankPlans?.length
          ? bankPlans
          : selectedBankPlans?.length
            ? selectedBankPlans
            : emi?.emiPlans || [];
      const normalized = plans.flatMap(normalizeTenor);

      return normalized.length
        ? normalized
        : emi?.emiTenors || [];
    }

    return emi?.emiTenors || [];
  }, [offerLogics]);

  const giftEmiTenors = useMemo(() => {
    const giftEmi = offerLogics?.GNG;
    const giftSelected =
      !!offerSelection.find((selection) => selection.type === "GNG")?.active &&
      selectedGiftIndexes.length > 0;

    if (!giftSelected || !giftEmi?.giftEmiEnabled) return emiTenors;

    const bankId = offerLogics?.EMI?.emiBankId;
    const bank = giftEmi.giftEmiBanks?.find(
      (giftBank) => bankId == null || giftBank.bankId === bankId,
    );
    const selectedBankTenors = bank?.tenors.filter((tenor) => tenor.selected);
    const allowedMonths =
      selectedBankTenors?.length
        ? selectedBankTenors.map((tenor) => Number(tenor.months ?? tenor.tenor))
        : giftEmi.giftEmiTenors || [];

    return allowedMonths.length
      ? emiTenors.filter((tenor) =>
          allowedMonths.includes(
            Number(tenor.tenor ?? tenor.name.split(" ")[0]),
          ),
        )
      : emiTenors;
  }, [emiTenors, offerLogics, offerSelection, selectedGiftIndexes]);

  const emiDisablesDiscount = useMemo(() => {
    if (offerLogics?.DISCOUNT?.mandatory) return false;

    const emiForbidsDiscount =
      offerLogics?.EMI?.forbiddens?.includes("DISCOUNT");
    const discountForbidsEmi =
      offerLogics?.DISCOUNT?.forbiddens?.includes("EMI");

    return !!(emiForbidsDiscount || discountForbidsEmi);
  }, [offerLogics]);

  const emiTabPrice = useMemo(() => {
    if (emiDisablesDiscount || !discountActive) {
      return {
        price: selectedSku?.price || skus[0]?.price,
        previousPrice: undefined,
      };
    } else {
      return {
        price: selectedSku?.discountedPrice || skus[0]?.discountedPrice,
        previousPrice: selectedSku?.price || skus[0]?.price,
      };
    }
  }, [emiDisablesDiscount, discountActive, selectedSku, skus]);

  const disableDiscount = useMemo(() => {
    if (offerLogics?.DISCOUNT?.mandatory) return false;

    for (const s of offerSelection) {
      if (s.type === "EMI") continue;

      if (!s.active) continue;

      const forbidsDiscount =
        offerLogics?.[s.type]?.forbiddens?.includes("DISCOUNT");
      const discountForbidsOffer = offerLogics?.DISCOUNT?.forbiddens?.includes(
        s.type,
      );

      if (forbidsDiscount || discountForbidsOffer) return true;
    }

    return false;
  }, [offerSelection, offerLogics]);

  const disableDiscount2 = useMemo(() => {
    if (offerLogics?.DISCOUNT?.mandatory) return false;

    for (const s of offerSelection) {
      if (!s.active) continue;

      const forbidsDiscount =
        offerLogics?.[s.type]?.forbiddens?.includes("DISCOUNT");
      const discountForbidsOffer = offerLogics?.DISCOUNT?.forbiddens?.includes(
        s.type,
      );

      if (forbidsDiscount || discountForbidsOffer) return true;
    }

    return false;
  }, [offerSelection, offerLogics]);

  // console.log("product details page offer logic", { offerLogics, offerSelection });

  const price = useMemo(() => {
    const basePrice =
      disableDiscount || !discountActive
        ? selectedSku?.price || skus[0]?.price
        : selectedSku?.discountedPrice || skus[0]?.discountedPrice;

    return {
      price: basePrice + totalGiftAmount,
      previousPrice:
        disableDiscount || !discountActive
          ? undefined
          : selectedSku?.price || skus[0]?.price,
    };
  }, [disableDiscount, discountActive, selectedSku, skus, totalGiftAmount]);

  const priceWithEmi = useMemo(() => {
    const basePrice =
      disableDiscount2 || !discountActive
        ? selectedSku?.price || skus[0]?.price
        : selectedSku?.discountedPrice || skus[0]?.discountedPrice;

    return {
      price: basePrice + totalGiftAmount,
      previousPrice:
        disableDiscount2 || !discountActive
          ? undefined
          : selectedSku?.price || skus[0]?.price,
    };
  }, [disableDiscount2, discountActive, selectedSku, skus, totalGiftAmount]);

  // console.log("product details page no payment",{price, priceWithEmi, disableDiscount, disableDiscount2});

  const attributeSelectMessage = useMemo(() => {
    if (selectedAttributes?.length < attributes?.length) {
      const key = attributes[selectedAttributes?.length]?.key;

      return (
        "** Select " +
        (key ? key : "variation") +
        (selectedAttributes?.length === 0 ? " First" : "") +
        " **"
      );
    }

    return "Select valid variation";
  }, [selectedAttributes, attributes]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setProductUrl(window.location.href);
    }

    if (!skus?.length) return;

    const defaultSku = skus[0];
    const defaultAttributes = (defaultSku?.attributes || []).map(
      (attr) => attr.value,
    );

    if (defaultAttributes.length > 0) {
      setSelectedAttributes((prev) => {
        if (prev.length > 0) return prev;
        return defaultAttributes;
      });
    }

    if (skus.length === 1) {
      attributes.forEach((attr, index) => {
        const option = attr.options[0];

        setSelectedAttributes((prev) => {
          const _prev = [...prev];
          _prev[index] = option;
          return _prev;
        });
      });
    }
  }, [attributes, skus]);

  useEffect(() => {
    for (const key of objectKeysTS(offerLogics)) {
      if (offerLogics[key]?.mandatory) {
        setOfferSelection((prev) => {
          const _prev = jsonParseSafe(JSON.stringify(prev)) as OfferSelection[];
          const index = _prev.findIndex((s) => s.type === key);
          if (index !== -1) _prev[index].active = true;
          return _prev;
        });

        if (key === "GNG") {
          setSelectedGiftIndexes(
            Array.from(
              Array(
                Math.min(
                  offerLogics[key]?.maxGifts || 0,
                  offerLogics[key].gifts?.length || 0,
                ),
              ).keys(),
            ),
          );
        }

        if (key === "BRAND") {
          setSelectedBrandGiftIndexes(
            Array.from(
              Array(
                Math.min(
                  offerLogics[key]?.maxGifts || 0,
                  offerLogics[key].gifts?.length || 0,
                ),
              ).keys(),
            ),
          );
        }
      }
    }
  }, [offerLogics]);

  useEffect(() => {
    const calcHeight = () => {
      if (pcRef.current) {
        const target = pcRef.current as HTMLDivElement;
        const height = target.clientHeight;
        setContainerHeight(height);
      }
    };

    calcHeight();

    setTimeout(calcHeight, 1000);

    window.addEventListener("resize", calcHeight);

    return () => {
      window.removeEventListener("resize", calcHeight);
    };
  }, [pcRef]);

  useEffect(() => {
    if (selectedSku && quantity > selectedSku.stockQuantity) {
      setQuantity(
        selectedSku.stockQuantity > 0 ? selectedSku.stockQuantity : 1,
      );
    }

    if (selectedSku && selectedSku.images?.length) {
      setActiveImageIndex(0);
    }
  }, [selectedSku, quantity]);

  useEffect(() => {
    attributes.forEach((attr, index) => {
      attr.options?.forEach((option, i) => {
        const invalid = invalidAttributeOptions[index]?.includes(i);

        if (invalid && selectedAttributes[index] === option)
          setSelectedAttributes((prev) => {
            const _prev = [...prev];
            return _prev.slice(0, index);
          });
      });
    });
  }, [invalidAttributeOptions, attributes, selectedAttributes]);

  const mouseMoveHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const rect = target.getBoundingClientRect();

    const r = 20;

    const dX = e.clientX - r - rect.left;
    const dY = e.clientY - r - rect.top;

    const pX = 100 * (dX / (rect.width - r * 2));
    const pY = 100 * (dY / (rect.height - r * 2));

    setHoverPos({
      // x: e.clientX - rect.left,
      x: pX,
      // y: e.clientY - rect.top
      y: pY,
    });
  };

  const mouseEnterHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    setShowZoom(true);

    const target = e.target as HTMLDivElement;
    target.addEventListener("mousemove", mouseMoveHandler as any);
  };

  const mouseLeaveHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    setShowZoom(false);

    const target = e.target as HTMLDivElement;
    target.removeEventListener("mousemove", mouseMoveHandler as any);
  };

  const offerSelecthandler = (type: OfferLogicType, flag?: boolean) => {
    setOfferSelection((prev) => {
      const _prev = jsonParseSafe(JSON.stringify(prev)) as OfferSelection[];
      const _index = _prev.findIndex((s) => s.type === type);

      const active = flag ?? !_prev[_index]?.active;

      if (_index === -1) {
        _prev.push({
          type: type,
          active: true,
        });
      } else {
        _prev[_index].active = active;
      }

      if (active) {
        offerLogics[type]?.forbiddens?.forEach((f) => {
          if (offerLogics[f]?.mandatory) return;

          const index = _prev.findIndex((s) => s.type === f);
          if (index !== -1) _prev[index].active = false;
        });
      }

      if (active) {
        offerLogics[type]?.mandatories?.forEach((f) => {
          if (offerLogics[f]?.mandatory) return;

          const index = _prev.findIndex((s) => s.type === f);
          if (index !== -1) _prev[index].active = true;
        });
      }

      return _prev;
    });
  };

  useEffect(() => {
    if (selectedSku) {
      if (selectedSku.preOrder) setStockStatus("Pre Order");
      else if (selectedSku.comingSoon) setStockStatus("Coming Soon");
      else if (selectedSku.stockOut || selectedSku.stockQuantity <= 0)
        setStockStatus("Stock Out");
      else if (selectedSku.stockQuantity > 0) setStockStatus("In Stock");
    } else {
      setStockStatus(product.stockStatus || "");
    }
  }, [selectedSku, product.stockStatus]);

  // discount countdown timer (use selected SKU or fallback to first SKU)
  useEffect(() => {
    const sku = selectedSku || skus?.[0];

    if (!sku) {
      setDiscountTimeLeft(null);
      setDiscountActive(false);
      return;
    }

    const hasDiscount =
      typeof sku.discountedPrice === "number" &&
      sku.discountedPrice > 0 &&
      sku.discountedPrice < (sku.price || 0);

    if (!hasDiscount) {
      setDiscountTimeLeft(null);
      setDiscountActive(false);
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const rawStart = sku.discountedPriceStart?.trim();
      const rawEnd = sku.discountedPriceEnd?.trim();

      if (rawStart) {
        const start = new Date(rawStart).getTime();
        if (!Number.isNaN(start) && now < start) {
          setDiscountTimeLeft(null);
          setDiscountActive(false);
          return;
        }
      }

      if (rawEnd) {
        const end = new Date(rawEnd).getTime();
        const diff = end - now;

        if (Number.isNaN(end) || diff <= 0) {
          setDiscountTimeLeft(null);
          setDiscountActive(false);
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        setDiscountActive(true);
        setDiscountTimeLeft({
          days: String(days).padStart(2, "0"),
          hours: String(hours).padStart(2, "0"),
          minutes: String(minutes).padStart(2, "0"),
          seconds: String(seconds).padStart(2, "0"),
        });
        return;
      }

      setDiscountActive(true);
      setDiscountTimeLeft(null);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [selectedSku, skus]);

  // view item event
  useEffect(() => {
    try {
      // Check if dataLayer is defined
      if (window && window.dataLayer && product) {
        // Clear previous ecommerce object
        window.dataLayer.push({ ecommerce: null });

        // Push new data to the dataLayer
        window.dataLayer.push({
          event: "view_item",
          ecommerce: {
            currency: "BDT",
            value:
              product?.skus?.[0]?.price === product?.skus?.[0]?.discountedPrice
                ? product.skus[0]?.price
                : product.skus[0]?.discountedPrice,
            items: [
              {
                item_id: product.skus?.[0]?.id?.toString(),
                item_name: product.name,
                affiliation: "Google Merchandise Store",
                coupon: "",
                discount:
                  product.skus?.[0]?.price - product.skus?.[0]?.discountedPrice,
                index: 0,
                item_brand: product.brand?.name,
                item_category: product?.category?.name,
                item_list_name: "Products",
                item_variant: product?.skus[0]?.attributes[0]?.value,
                location_id: "ChIJIQBpAG2ahYAR_6128GcTUEo",
                price: product.skus[0]?.price,
                quantity: 1,
              },
            ],
          },
        });
      }
    } catch (err) { }
  }, [product]);

  // add to cart event
  useEffect(() => {
    try {
      // Check if dataLayer is defined
      if (window && window.dataLayer && product && selectedSku) {
        // Clear previous ecommerce object
        window.dataLayer.push({ ecommerce: null });

        // Push new data to the dataLayer
        window.dataLayer.push({
          event: "add_to_cart",
          ecommerce: {
            currency: "BDT",
            value:
              selectedSku?.price === selectedSku.discountedPrice
                ? selectedSku.price
                : selectedSku.discountedPrice,
            items: [
              {
                item_id: selectedSku
                  ? selectedSku?.id?.toString()
                  : product?.skus[0]?.id.toString(),
                item_name: product.name,
                affiliation: "Google Merchandise Store",
                coupon: "",
                discount: selectedSku
                  ? selectedSku?.price - selectedSku.discountedPrice
                  : 0,
                index: 0,
                item_brand: product.brand?.name,
                item_category: product.category?.name,
                item_list_name: "Products",
                item_variant: selectedSku
                  ? selectedSku?.attributes[0]?.value
                  : product?.skus[0]?.attributes[0]?.value,
                location_id: "ChIJIQBpAG2ahYAR_6128GcTUEo",
                price: selectedSku
                  ? selectedSku?.price
                  : product?.skus[0]?.price,
                quantity: quantity,
              },
            ],
          },
        });
      }
    } catch (err) { }
  }, [product, selectedSku, quantity]);

  const classNames = [classes.Top, className];

  const activeImage = images[activeImageIndex];

  // ── Compare handlers ──
  const handleAddCompare = useCallback(() => {
    if (!product?.id || !product?.name) {
      return;
    }

    const result = upsertCompareProduct({
      id: Number(product.id),
      name: product.name,
      thumbnail: product.thumbnail || activeImage || null,
      slug: product.slug || null,
    });

    if (!result.saved) {
      toast.info(
        `You can compare up to ${MAX_COMPARE_PRODUCTS} products at a time.`,
      );
      return;
    }

    // Show a small success modal when a product is added to compare.
    setCompareAddedProductName(product.name);
    setCompareAddSuccessOpen(true);

    // auto-close after 6 seconds
    if (compareSuccessTimerRef.current) {
      window.clearTimeout(compareSuccessTimerRef.current);
    }
    compareSuccessTimerRef.current = window.setTimeout(() => {
      setCompareAddSuccessOpen(false);
      compareSuccessTimerRef.current = null;
    }, 6000);
  }, [
    activeImage,
    product?.id,
    product?.name,
    product?.slug,
    product?.thumbnail,
  ]);

  const handleRedirectCompare = useCallback(() => {
    setCompareAddSuccessOpen(false);

    const compareProducts = readCompareProducts();
    const ids = compareProducts
      .map((item) => Number(item.id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (ids.length === 0) {
      router.push("/compare");
      return;
    }

    router.push(`/compare?deviceIds=${ids.join(",")}`);
  }, [router]);

  const productPoint =
    (product?.pointEarn || 0) + (product?.additionalPointEarn || 0);

  return (
    <div className={classNames.join(" ")} style={style}>
      <div className={`${classes.container} container`}>
        <Breadcumb items={breadcumb} />

        <div className={classes.contents}>
          {/* left contents */}
          <div className={classes.left}>
            {/* product images changer slider */}
            <div className="w-full flex justify-center flex-col-reverse lg:flex-row relative gap-2">
              <div className={classes.changer}>
                <ArrowBoxButton
                  key={"prev-product-image"}
                  id={"prev-product-image-361"}
                  type="left"
                  className={`${classes.button} ${classes.top}`}
                />

                <ArrowBoxButton
                  key={"next-produt-image"}
                  id={"next-produt-image-361"}
                  type="right"
                  className={`${classes.button} ${classes.bottom}`}
                />
                <Swiper
                  key={selectedSku?.sku}
                  spaceBetween={4}
                  slidesPerView={3.5}
                  slidesPerGroup={2}
                  navigation={{
                    nextEl: "#next-produt-image-361",
                    prevEl: "#prev-product-image-361",
                  }}
                  autoplay={{
                    delay: 2500,
                    disableOnInteraction: true,
                  }}
                  breakpoints={{
                    768: {
                      direction: "vertical",
                    },
                  }}
                  modules={[Navigation]}
                  className={classes.swiper}
                  direction="horizontal"
                  style={{
                    ...(containerHeight && {
                      height: containerHeight + "px",
                    }),
                  }}
                >
                  {images.map((image, i) => (
                    <SwiperSlide key={i}>
                      <Image
                        src={getS3ImageUrl(image, "MEDIA")}
                        alt={product.name ?? "product image"}
                        layout="responsive"
                        width={800}
                        height={800}
                        style={{
                          aspectRatio: 1,
                          objectFit: "cover",
                        }}
                        className={`${classes.image} ${activeImageIndex === i && classes.active
                          }`}
                        onClick={() => setActiveImageIndex(i)}
                        unoptimized={true}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              {/* product main image */}
              <div
                className={classes.mainImage}
                id={productImageId}
                onMouseEnter={mouseEnterHandler}
                onMouseLeave={mouseLeaveHandler}
              >
                <Image
                  src={getS3ImageUrl(activeImage, "MEDIA")}
                  alt={product.name ?? "product image"}
                  layout="responsive"
                  width={800}
                  height={800}
                  style={{
                    aspectRatio: 1,
                    objectFit: "contain",
                  }}
                  className={classes.image}
                  ref={pcRef}
                  unoptimized={true}
                />

                {product.isOnline && (
                  <div className="absolute left-0 top-0 z-50">
                    <Image
                      src={newArrival}
                      alt="online only"
                      width={100}
                      height={100}
                      unoptimized={true}
                    />
                  </div>
                )}

                {/* {product.isOnline && (
                <div className="absolute right-3 top-3 z-50">
                  <Image
                    src={onlineOnly}
                    alt="online only"
                    width={100}
                    height={100}
                    unoptimized={true}
                  />
                </div>
              )} */}
                {/* discount tag */}
                {/* <div className={classes.tag}>
                                <DiscountOutlineIcon width={20} height={20} className={classes.icon} />

                                <span className={classes.text}>-15 %</span>
                            </div> */}
              </div>
              {/* {productAds.response?.Data && (
              <div className="my-4">
                {productAds.response.Data.link ? (
                  <Link
                    href={getPageRoute(
                      (productAds.response.Data.type as PageTypes) || "custom",
                      productAds.response.Data.link
                    )}
                  >
                    <Image
                      src={getS3ImageUrl(
                        productAds.response.Data.image_url || ""
                      )}
                      alt={productAds.response.Data.title || "Ad"}
                      width={800}
                      height={200}
                      layout="responsive"
                      unoptimized={true}
                      style={{ objectFit: "contain" }}
                    />
                  </Link>
                ) : (
                  <Image
                    src={getS3ImageUrl(
                      productAds.response.Data.image_url || ""
                    )}
                    alt={productAds.response.Data.title || "Ad"}
                    width={800}
                    height={200}
                    layout="responsive"
                    unoptimized={true}
                    style={{ objectFit: "contain" }}
                  />
                )}
              </div>
            )} */}
            </div>

            {/* The image column is flex-col-reverse on mobile (thumbnails under
                the main image), which also flips this warranty row to the top.
                -order-1 puts it first in flow = LAST visually in the reversed
                column; md:order-none restores the desktop position. */}
            <div className="flex w-full -order-1 md:order-none">
              {/* Left spacing */}
              <div className="hidden w-[19%] sm:block" />

              {/* Warranty */}
              {product.warrantyType && product.warranty && (
                <div
                  className="flex w-full min-w-0 items-center gap-2 rounded-md border border-[#f26e21]/50 bg-[#fffaf7] px-2 py-2 sm:w-[83%] sm:gap-3 sm:px-3"
                  title={product.warrantyType}
                >
                  {/* Icon */}
                  <Image
                    src={warrantyImg}
                    alt="Warranty"
                    width={48}
                    height={48}
                    className="h-9 w-9 shrink-0 object-contain sm:h-8 sm:w-8"
                  />

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-bold text-primary-dark sm:text-[15px]">
                      {product.warranty}
                    </p>

                    {product.warrantySubtitle && (
                      <p className="whitespace-pre-line break-words text-[10px] text-gray4 sm:text-xs">
                        {product.warrantySubtitle}
                      </p>
                    )}
                  </div>

                  {/* View Details */}
                  {product.warrantyPolicy &&
                    (String((product as any).warrantyLinkType).toLowerCase() ===
                      "link" ? (
                      <a
                        href={product.warrantyPolicy}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-0.5 shrink-0 whitespace-nowrap text-[10px] font-semibold text-primary underline underline-offset-2 sm:text-xs"
                      >
                        View Details
                        <span className="ml-1 text-sm no-underline sm:text-base">
                          ›
                        </span>
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="mt-0.5 shrink-0 whitespace-nowrap text-[10px] font-semibold text-primary underline underline-offset-2 sm:text-xs"
                        onClick={() => setShowWarrantyPolicy(true)}
                      >
                        View Details
                        <span className="ml-1 text-sm no-underline sm:text-base">
                          ›
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            {productAds.response?.Data && (
              <div className="my-4">
                {productAds.response.Data.link ? (
                  <Link
                    href={getPageRoute(
                      (productAds.response.Data.type as PageTypes) || "custom",
                      productAds.response.Data.link,
                    )}
                  >
                    <Image
                      src={getS3ImageUrl(
                        productAds.response.Data.image_url || "",
                      )}
                      alt={productAds.response.Data.title || "Ad"}
                      width={800}
                      height={200}
                      layout="responsive"
                      unoptimized={true}
                      style={{ objectFit: "contain" }}
                      className="w-full h-[300px] md:h-[200px] lg:h-[200px] xl:h-[200px] 2xl:h-[200px]"
                    />
                  </Link>
                ) : (
                  <Image
                    src={getS3ImageUrl(
                      productAds.response.Data.image_url || "",
                    )}
                    alt={productAds.response.Data.title || "Ad"}
                    width={800}
                    height={200}
                    layout="responsive"
                    unoptimized={true}
                    style={{ objectFit: "contain" }}
                    className="w-full h-[300px] md:h-[200px] lg:h-[200px] xl:h-[200px] 2xl:h-[200px]"
                  />
                )}
              </div>
            )}
          </div>

          {/* right contents */}
          <div className={classes.right}>
            {/* product image zoom preview */}
            {showZoom && (
              <div
                className={classes.imageViewer}
                style={{
                  height: containerHeight + "px",
                }}
              >
                <Image
                  src={getS3ImageUrl(activeImage, "MEDIA")}
                  alt={product.name ?? "product image"}
                  blurDataURL={
                    "https://www.unfe.org/wp-content/uploads/2019/04/SM-placeholder.png"
                  }
                  placeholder="blur"
                  layout="fill"
                  objectFit="contain"
                  objectPosition="center"
                  className={classes.viewImage}
                  unoptimized={true}
                  style={{
                    transformOrigin: `${hoverPos.x}% ${hoverPos.y}%`,
                  }}
                />
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              {!!product.brand && (
                <div className={`${classes.row} !mb-4`}>
                  <Link
                    // ${classes.brand}
                    href={`${routes.brand}${product.brand.slug}`}
                  >
                    {/* <p className={classes.name}>Brand :</p> */}

                    {product.brand?.logo ? (
                      <Image
                        src={getS3ImageUrl(
                          formatImagePath(product.brand.logo),
                          "MEDIA",
                        )}
                        alt={product.brand.name ?? "brand logo"}
                        width={70}
                        height={40}
                        className={classes.brand}
                        unoptimized={true}
                        style={{
                          // aspectRatio: 1,
                          objectFit: "contain",
                          maxHeight: "45px", // 35px
                        }}
                      />
                    ) : (
                      <span className="uppercase border border-primary text-sm px-2 py-0.5 rounded-sm">
                        {product.brand.name}
                      </span>
                    )}
                  </Link>
                </div>
              )}
              <button
                type="button"
                className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-sm outline-gray-300 transition-all hover:bg-gray-100 hover:text-primary mr-2"
                onClick={handleAddCompare}
              >
                <PlusOutlineIcon width={16} height={16} />
                Add to Compare
              </button>
            </div>

            <div className={classes.row + " !gap-4 pr-2"}>
              <h1 className={classes.productName}>
                {convertNameChar(product.name)}
              </h1>

              <ShareButton
                shareUrl={productUrl}
                description={product.shortDescription}
                image={getS3ImageUrl(product.thumbnail)}
              />
            </div>

            <div className={classes.ratingContainer}>
              <p className="text-neutral-500 font-bold">
                Status: <span className="font-normal">{stockStatus}</span>
              </p>

              {product?.review?.totalReview &&
                product.review?.totalReview > 0 ? (
                <RatingStars
                  rating={product.review.avgRating as number}
                  size="sm"
                  className="py-0.5"
                />
              ) : (
                <p className="text-[0.75rem] md:text-[0.83rem] text-primary-dark/80 font-semibold">
                  No Review Yet
                </p>
              )}
            </div>
            {selectedSku && !outOfStock && discountTimeLeft && (
              <div className="flex items-center gap-1.5 text-sm bg-red-50 border border-red-200 rounded px-3 py-1.5 mb-2 w-00 max-w-[300px]">
                <span className="text-red-600 font-medium text-xs">
                  ⏳ Offer ends in:
                </span>
                <span className="bg-red-500 text-white px-1.5 py-0.5 rounded font-mono text-xs font-bold">
                  {discountTimeLeft.days}d
                </span>
                <span className="bg-red-500 text-white px-1.5 py-0.5 rounded font-mono text-xs font-bold">
                  {discountTimeLeft.hours}h
                </span>
                <span className="bg-red-500 text-white px-1.5 py-0.5 rounded font-mono text-xs font-bold">
                  {discountTimeLeft.minutes}m
                </span>
                <span className="bg-red-500 text-white px-1.5 py-0.5 rounded font-mono text-xs font-bold">
                  {discountTimeLeft.seconds}s
                </span>
              </div>
            )}

            <div
              className={`flex flex-col lg:flex-row lg:items-center ${comingSoon ? "gap-4 lg:gap-2" : "gap-4 lg:gap-8"
                }`}
            >
              <div className="space-y-4">
                {product.status === "active" &&
                  price.price > 0 &&
                  (comingSoon ||
                    stockStatus == "In Stock" ||
                    stockStatus == "Pre Order") && (
                    <CardPriceTag
                      price={!emiSelected ? price.price : priceWithEmi.price}
                      previousPrice={
                        !emiSelected
                          ? price.previousPrice
                          : priceWithEmi.previousPrice
                      }
                      size="lg"
                      className="!justify-start"
                    />
                  )}
                {product.status === "active" &&
                  !comingSoon &&
                  stockStatus == "Stock Out" && (
                    <span className="text-2xl font-bold bg-red-100 text-primary px-3 py-1 rounded-md inline-block">
                      TBA
                    </span>
                  )}
              </div>
              {/* <div className="space-y-4">
                {product.status === "active" && !comingSoon && (
                  <CardPriceTag
                    price={!emiSelected ? price.price : priceWithEmi.price}
                    previousPrice={
                      !emiSelected
                        ? price.previousPrice
                        : priceWithEmi.previousPrice
                    }
                    size="lg"
                    className="!justify-start"
                  />
                )}
              </div> */}

              <div className="flex">
                <Link
                  href={`https://wa.me/+8801318236991?text=I want to know more about *${product.name}*%0a%0aURL: ${productUrl}`}
                  className=""
                  target="_blank"
                >
                  <Image
                    src={whatsappImg}
                    alt="share via WhatsApp"
                    width={150}
                  />
                </Link>
              </div>
            </div>

            {productPoint && productPoint > 0 ? (
              <p className="text-sm text-primary">
                Earn {productPoint} Reward Points
              </p>
            ) : null}

            <div
              className="html-content overflow-auto hide-scrollbar last:!mb-0 !mb-0 [&>*]:mb-0 [&_*]:text-sm md:[&_*]:!text-base"
              style={{
                ...(attributes?.length > 0 && {
                  paddingBottom: "16px",
                }),
              }}
              dangerouslySetInnerHTML={{
                __html: product.shortDescription,
              }}
            ></div>

            {/*  */}

            {attributes.map((attr, index) => {
              const disabled =
                index > 0 && selectedAttributes[index - 1] === undefined;
              const colorOnlyAttribute = attr.types.every(
                (type) => type === "code",
              );

              return (
                <div
                  className={`${classes.row} ${classes.attribute} ${attr.key.toLowerCase() === "color"
                    ? classes.colorAttribute
                    : ""
                    } ${disabled ? classes.disabled : ""
                    }`}
                  key={attr.key}
                  style={{
                    ...(index === attributes?.length - 1 && {
                      marginBottom:
                        attr.key.toLowerCase() === "color" ? 10 : 5,
                    }),
                  }}
                >
                  <p className={`${classes.name} ${attr.key.toLowerCase() === "color" ? 'mb-2' : ''}`}>
                    {attr.key}:
                    {attr.key.toLowerCase() === "color" &&
                      selectedAttributes[index] && (
                        <span className="ml-1 font-normal">
                          {selectedAttributes[index]}
                        </span>
                      )}
                  </p>

                  <div
                    className={`${classes.options} ${colorOnlyAttribute ? classes.color : ""
                      }`}
                  >
                    {attr.options.map((option, i) => {
                      const optionDisabled =
                        invalidAttributeOptions[index]?.includes(i);
                      const selected = selectedAttributes[index] === option;
                      const optionType = attr.types[i];
                      const visual = attr.visuals[i];

                      let classNames = `${classes.option} `;

                      if (selected) classNames += ` ${classes.active}`;
                      if (optionType === "image" || optionType === "code")
                        classNames += ` ${classes.color}`;

                      return (
                        <div key={i} className="flex flex-col items-center">
                          <button
                            disabled={disabled || optionDisabled}
                            title={option}
                            onClick={() => {
                              setSelectedAttributes((prev) => {
                                const next = [...prev];
                                next[index] = option;
                                return next;
                              });
                              setShowVariationError(false);
                            }}
                            className={`
                            relative
                            rounded-lg
                            border
                            bg-white
                            overflow-hidden
                            transition-all
                            duration-200
                            ${optionType === "text"
                                ? "h-[40px] w-auto px-3"
                                : "w-[80px] h-[80px]"
                              }
                            ${selectedAttributes[index] === option
                                ? "border-primary ring-2 ring-primary/30"
                                : "border-gray-300"
                              }
                            ${disabled || optionDisabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400 hover:ring-2 hover:ring-gray-400/30"}
                          `}
                          >
                            {optionType === "image" ? (
                              <div className="flex items-center justify-center w-full h-full p-1">
                                <Image
                                  src={getS3ImageUrl(visual, "MEDIA")}
                                  alt={option ?? "option image"}
                                  width={70}
                                  height={70}
                                  unoptimized
                                  className="rounded-md object-cover w-[70px] h-[70px]"
                                />
                              </div>
                            ) : optionType === "code" && attr.skuImages?.[i] ? (
                              <div className="flex items-center justify-center w-full h-full p-1">
                                <Image
                                  src={getS3ImageUrl(
                                    attr.skuImages[i]!,
                                    "MEDIA",
                                  )}
                                  alt={option}
                                  width={70}
                                  height={70}
                                  unoptimized
                                  className="rounded-md object-cover w-[70px] h-[70px]"
                                />
                              </div>
                            ) : optionType === "code" ? (
                              <div className="flex items-center justify-center w-full h-full">
                                <span
                                  className="w-10 h-10 rounded-full border border-gray-300"
                                  style={{ backgroundColor: visual }}
                                />
                              </div>
                            ) : (
                              <div className="flex h-full w-full items-center justify-center px-2">
                                <p className="text-sm font-medium">{option}</p>
                              </div>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {selectedSku && (
              <div className="flex gap-4 items-center mt-8 !mb-0 flex-wrap">
                {!outOfStock && (
                  <QuantityChange
                    quantity={quantity}
                    maxQuantity={selectedSku?.stockQuantity}
                    addQuantity={() => setQuantity((prev) => prev + 1)}
                    price={selectedSku?.discountedPrice}
                    removeQuantity={() =>
                      setQuantity((prev) => Math.max(prev - 1, 1))
                    }
                  />
                )}

                {selectedSku?.customSku && (
                  <p className="text-sm font-medium">
                    SKU:{" "}
                    <span className="text-base font-normal">
                      {selectedSku?.customSku}
                    </span>
                  </p>
                )}

                {selectedSku?.modelNo && (
                  <p className="text-sm font-medium">
                    Model No:{" "}
                    <span className="text-base font-normal">
                      {selectedSku?.modelNo}
                    </span>
                  </p>
                )}
              </div>
            )}

            {product.status === "active" &&
              emiActive &&
              !outOfStock &&
              !comingSoon && (
                <div
                  className="border border-primary/25  rounded cursor-pointer w-[40%] lg:w-[30%] px-3 py-1 text-sm text-primary font-medium hover:bg-primary/95 hover:text-white transition-all mt-5 text-center"
                  onClick={() => setShowEmiPlans(true)}
                >
                  Emi View Plans
                </div>
              )}



            {/* payment tab checkbox */}
            {product.status === "active" && !comingSoon && (
              <div className={classes.gifts}>
                {offerSelectItems.map((item, index) => {
                  if (!offerLogics[item.type]?.selected) return null;

                  const active = !!offerSelection.find(
                    (s) => s.type === item.type,
                  )?.active;

                  return (
                    <div className={`${classes.gift}`} key={index}>
                      <CheckBox
                        checked={active}
                        onClick={() => {
                          if (
                            (item.type === "GNG" || item.type === "BRAND") &&
                            !active &&
                            ((item.type === "GNG" &&
                              selectedGiftIndexes?.length === 0) ||
                              (item.type === "BRAND" &&
                                selectedBrandGiftIndexes?.length === 0))
                          ) {
                            setShowGiftSelect(item.type);
                          } else {
                            offerSelecthandler(item.type);
                          }
                        }}
                        disabled={
                          offerSelection.find((s) => s.type === item.type)
                            ?.disabled
                        }
                      />
                      <p className={classes.name}>{item.label}</p>

                      {/* {index === 0 && (
											<button
												className={classes.emiButton}
												onClick={() =>
													setShowEmiPlans(true)
												}>
												<p className={classes.text}>
													View Banks
												</p>

												<RightIcon
													className={classes.icon}
													width={16}
													height={16}
												/>
											</button>
										)} */}
                    </div>
                  );
                })}
              </div>
            )}

            {/* payment tab */}
            {product.status === "active" &&
              emiActive &&
              !outOfStock &&
              !comingSoon && (
                <div className={classes.paymentTabs}>
                  <PaymentTab
                    active={!emiSelected}
                    onClick={() => {
                      if (
                        offerSelection.find((s) => s.type === "EMI")
                          ?.disabled ||
                        !emiSelected
                      )
                        return;

                      offerSelecthandler("EMI");
                    }}
                    price={price.price}
                    prePrice={price.previousPrice}
                    discount={!disableDiscount}
                    emi={false}
                    emiTenors={emiTenors}
                  />

                  <PaymentTab
                    active={emiSelected}
                    onClick={() => {
                      if (
                        offerSelection.find((s) => s.type === "EMI")
                          ?.disabled ||
                        emiSelected
                      )
                        return;

                      offerSelecthandler("EMI");
                    }}
                    price={emiTabPrice.price}
                    emiType={emiType}
                    prePrice={emiTabPrice.previousPrice}
                    discount={!emiDisablesDiscount}
                    emi={true}
                    emiTenors={giftEmiTenors}
                  />
                </div>
              )}

            {showVariationError && (
              <button
                className={`${classes.button} ${classes.invalid}`}
                disabled
              >
                {attributeSelectMessage}
              </button>
            )}

            {preOrder && (
              <p className="text-primary font-medium">
                Pre-order amount Tk.{" "}
                {(selectedSku?.preOrderPer || 0) * (quantity || 1)}
              </p>
            )}

            {comingSoon && (
              <div className="mt-4">
                <button
                  className={`${classes.button} ${classes.invalid} border border-gray-700 !text-gray-700 rounded-md !px-6 !py-1`}
                >
                  Coming Soon
                </button>
              </div>
            )}

            {/* admin actions */}
            {showAdminActions && (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-primary/20 pt-4">
                <button
                  onClick={() => setShowSkuModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
                >
                  Update SKU
                </button>

                <button
                  onClick={async () => {
                    setGiftFormLoading(true);
                    try {
                      const res = await getProductGifts(Number(product.id));
                      if (
                        res?.Data?.gift &&
                        typeof jsonParseSafe(res?.Data?.gift) === "object"
                      ) {
                        setOfferLogicsState(jsonParseSafe(res?.Data?.gift));
                      } else {
                        // Fallback: use the product.gift from the product data
                        const fallback =
                          typeof product.gift === "string"
                            ? jsonParseSafe(product.gift)
                            : product.gift;
                        if (fallback && typeof fallback === "object") {
                          setOfferLogicsState(fallback);
                        }
                      }
                      setShowGiftForm(true);
                    } catch (err) {
                      console.error("Failed to fetch product gifts", err);
                      // Fallback: use product.gift
                      const fallback =
                        typeof product.gift === "string"
                          ? jsonParseSafe(product.gift)
                          : product.gift;
                      if (fallback && typeof fallback === "object") {
                        setOfferLogicsState(fallback);
                      }
                      setShowGiftForm(true);
                    } finally {
                      setGiftFormLoading(false);
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
                >
                  {giftFormLoading ? "Loading..." : "Update Gift"}
                </button>
              </div>
            )}

            {/* cta buttons */}
            {!comingSoon && (
              <div>
                {product.status === "active" ? (
                  <div className={classes.buttons}>
                    {!outOfStock && !preOrder ? ( // && !existOnCart && selectedSku
                      !selectedSku && hasPreOrder ? (
                        <Button
                          className={`${classes.button} ${classes.preOrder}`}
                          onClick={() => {
                            if (selectedSku) {
                              onBuyNow(selectedSku, quantity, {
                                discount: !disableDiscount,
                                brand_gift: offerSelection.find(
                                  (s) => s.type === "BRAND",
                                )?.active
                                  ? selectedBrandGifts
                                  : undefined,
                                gng_gift: offerSelection.find(
                                  (s) => s.type === "GNG",
                                )?.active
                                  ? selectedGngGifts
                                  : undefined,
                                emi: !!emiSelected,
                                cashbackAmount: cashbackAmount || undefined,
                              });
                            } else {
                              setShowVariationError(true);
                            }
                          }}
                        >
                          Pre Order
                        </Button>
                      ) : (
                        <>
                          {!emiSelected &&
                            selectedSku?.discountedPrice &&
                            selectedSku?.discountedPrice > 1 && (
                              <button
                                className={`${classes.button} ${classes.addToCart}`}
                                disabled={cartLoading}
                                onClick={() => {
                                  const discount = !disableDiscount;

                                  // return;

                                  if (selectedSku) {
                                    onAddToCart(selectedSku, quantity, {
                                      discount,
                                      brand_gift: offerSelection.find(
                                        (s) => s.type === "BRAND",
                                      )?.active
                                        ? selectedBrandGifts
                                        : undefined,
                                      gng_gift: offerSelection.find(
                                        (s) => s.type === "GNG",
                                      )?.active
                                        ? selectedGngGifts
                                        : undefined,
                                      emi: !!emiSelected,
                                      cashbackAmount:
                                        cashbackAmount || undefined,
                                    });
                                  } else {
                                    setShowVariationError(true);
                                  }
                                }}
                              >
                                {cartLoading && <Loader />}
                                Add To Cart
                              </button>
                            )}

                          <button
                            className={`${classes.button} ${classes.buyNow}`}
                            disabled={cartLoading}
                            onClick={() => {
                              const discount = emiSelected
                                ? !disableDiscount2
                                : !disableDiscount;

                              if (selectedSku) {
                                onBuyNow(selectedSku, quantity, {
                                  discount,
                                  brand_gift: offerSelection.find(
                                    (s) => s.type === "BRAND",
                                  )?.active
                                    ? selectedBrandGifts
                                    : undefined,
                                  gng_gift: offerSelection.find(
                                    (s) => s.type === "GNG",
                                  )?.active
                                    ? selectedGngGifts
                                    : undefined,
                                  emi: !!emiSelected,
                                  cashbackAmount: cashbackAmount || undefined,
                                });
                              } else {
                                setShowVariationError(true);
                              }
                            }}
                          >
                            Buy Now
                            {cartLoading && <Loader />}
                          </button>
                        </>
                      )
                    ) : (
                      <>
                        {preOrder ? (
                          <Button
                            className={`${classes.button} ${classes.preOrder}`}
                            onClick={() => {
                              if (selectedSku) {
                                onBuyNow(selectedSku, quantity, {
                                  discount: !disableDiscount,
                                  brand_gift: offerSelection.find(
                                    (s) => s.type === "BRAND",
                                  )?.active
                                    ? selectedBrandGifts
                                    : undefined,
                                  gng_gift: offerSelection.find(
                                    (s) => s.type === "GNG",
                                  )?.active
                                    ? selectedGngGifts
                                    : undefined,
                                  emi: !!emiSelected,
                                  cashbackAmount: cashbackAmount || undefined,
                                });
                              } else {
                                setShowVariationError(true);
                              }
                            }}
                          >
                            Pre Order
                          </Button>
                        ) : (
                          <button
                            className={`${classes.button} ${classes.invalid}`}
                            disabled
                          >
                            {/* existOnCart
												? "Already on Cart"
												: */}
                            {outOfStock
                              ? "Out of Stock"
                              : attributeSelectMessage}
                          </button>
                        )}

                        {outOfStock && (
                          <Button
                            btnType="outline"
                            className={`${classes.button} ${classes.notify} flex justify-center`}
                            onClick={() => {
                              dispatch(
                                setGlobalPopupElement(
                                  <PopupManager>
                                    <NotifyMe
                                      onClose={() =>
                                        dispatch(setGlobalPopupElement(null))
                                      }
                                      onSubmit={(data) => {
                                        postNotifyMe({
                                          phone: data.phone,
                                          email: data.email,
                                          productSkuId: selectedSku?.id ?? 0,
                                          name: name,
                                        })
                                          .then((data) => {
                                            toastMe(
                                              "We will notify you when this product is available.",
                                              "info",
                                            );
                                          })
                                          .finally(() => {
                                            dispatch(
                                              setGlobalPopupElement(null),
                                            );
                                          });
                                      }}
                                    />
                                  </PopupManager>,
                                ),
                              );
                            }}
                          >
                            <NotificationOutlineIcon
                              width={22}
                              height={22}
                              className={classes.icon}
                            />
                            <p className={classes.text}>Notify Me</p>
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className={classes.buttons}>
                    <button className="bg-gray-100 text-gray-600 px-6 py-2 rounded-md font-medium">
                      Not Available
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={classes.rightGift}
            style={{
              ...(hasGngGifts || hasBrandGifts
                ? {
                  display: "block",
                }
                : {
                  display: "none",
                }),
            }}
          >
            {hasGngGifts ? (
              <GiftSelect
                title="GIFT FROM G&G"
                type="gng"
                max={offerLogics["GNG"]?.maxGifts}
                giftRes={gngGift}
                giftItems={gngGiftItems}
                selected={selectedGiftIndexes}
                setSelected={(indexes) => {
                  setSelectedGiftIndexes(indexes);

                  if (indexes?.length > 0) {
                    offerSelecthandler("GNG", true);
                  } else {
                    offerSelecthandler("GNG", false);
                  }
                }}
              />
            ) : null}

            {hasBrandGifts ? (
              <GiftSelect
                title={`GIFT FROM ${product.brand?.name || "Brand"}`}
                subTitle="Select your free gift"
                type="brand"
                max={offerLogics["BRAND"]?.maxGifts}
                giftRes={brandGift}
                giftItems={brandGiftItems}
                selected={selectedBrandGiftIndexes}
                setSelected={(indexes) => {
                  setSelectedBrandGiftIndexes(indexes);

                  if (indexes?.length > 0) {
                    offerSelecthandler("BRAND", true);
                  } else {
                    offerSelecthandler("BRAND", false);
                  }
                }}
              />
            ) : null}
          </div>
        </div>
      </div>

      {showEmiPlans && (
        <PopupManager onClose={() => setShowEmiPlans(false)}>
          <EMIOptions
            price={Number(emiTabPrice.price || 0)}
            slug={product?.slug || ""}
          />
        </PopupManager>
      )}

      {showGiftSelect && (
        <PopupManager onClose={() => setShowGiftSelect(undefined)}>
          <div className={classes.giftPopup}>
            <GiftSelect
              max={
                showGiftSelect === "GNG"
                  ? offerLogics["GNG"]?.maxGifts
                  : offerLogics["BRAND"]?.maxGifts
              }
              giftRes={showGiftSelect === "GNG" ? gngGift : brandGift}
              selected={
                showGiftSelect === "GNG"
                  ? selectedGiftIndexes
                  : selectedBrandGiftIndexes
              }
              setSelected={
                showGiftSelect === "GNG"
                  ? setSelectedGiftIndexes
                  : setSelectedBrandGiftIndexes
              }
              giftItems={
                showGiftSelect === "GNG" ? gngGiftItems : brandGiftItems
              }
              title={
                showGiftSelect === "GNG" ? "GIFT FROM G&G" : "GIFT FROM BRAND"
              }
              type={showGiftSelect === "GNG" ? "gng" : "brand"}
            />

            <div className={classes.buttons}>
              <Button
                btnType="primary"
                className={classes.btn}
                onClick={() => {
                  offerSelecthandler(showGiftSelect);
                  setShowGiftSelect(undefined);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </PopupManager>
      )}

      {compareAddSuccessOpen && (
        <PopupManager
          onClose={() => {
            setCompareAddSuccessOpen(false);
            setCompareAddedProductName(null);
            if (compareSuccessTimerRef.current) {
              window.clearTimeout(compareSuccessTimerRef.current);
              compareSuccessTimerRef.current = null;
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-[400px] max-w-[90vw] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Success</h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => {
                  setCompareAddSuccessOpen(false);
                  setCompareAddedProductName(null);
                  if (compareSuccessTimerRef.current) {
                    window.clearTimeout(compareSuccessTimerRef.current);
                    compareSuccessTimerRef.current = null;
                  }
                }}
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                <strong>Success:</strong> You have added&nbsp;
                <strong>{compareAddedProductName}</strong>&nbsp;to your product
                comparison!
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="bg-primary text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
                onClick={handleRedirectCompare}
              >
                Compare Now
              </button>
              <button
                type="button"
                className="border border-gray-300 text-gray-700 px-5 py-2 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setCompareAddSuccessOpen(false);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </PopupManager>
      )}

      {/* Admin: Update SKU Modal */}
      {showSkuModal && (
        <UpdateSkuModal
          productId={product.id}
          onClose={() => setShowSkuModal(false)}
        />
      )}

      {/* Admin: Update Gift Modal */}
      {showGiftForm && (
        <PopupManager onClose={() => setShowGiftForm(false)}>
          <div className="bg-white rounded-lg shadow-xl w-[700px] max-w-[95vw] max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Update Gift - {product.name}
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowGiftForm(false)}
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <OfferLogicsForm
              offersState={offerLogicsState}
              setOffersState={setOfferLogicsState}
            />

            {/* Save (submits a moderated update request) */}
            <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-200">
              <button
                type="button"
                className="px-5 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
                onClick={() => setShowGiftForm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={giftSaving}
                onClick={handleSaveGift}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition-colors"
              >
                {giftSaving ? "Submitting..." : "Save Gift"}
              </button>
            </div>
          </div>
        </PopupManager>
      )}

      {showWarrantyPolicy && (
        <PopupManager onClose={() => setShowWarrantyPolicy(false)}>
          <div className="w-[520px] max-w-[calc(100vw-2rem)] rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between gap-4">
              <h3 className="text-lg font-semibold text-primary-dark">
                {product.warranty}
              </h3>
              {/* <button
                type="button"
                aria-label="Close warranty details"
                className="text-2xl leading-none text-gray-400 hover:text-gray-700"
                onClick={() => setShowWarrantyPolicy(false)}
              >
                ×
              </button> */}
            </div>
            <p className="whitespace-pre-line text-sm leading-6 text-gray-600">
              {product.warrantyPolicy}
            </p>
          </div>
        </PopupManager>
      )}
    </div>
  );
};

const PaymentTab = ({
  price,
  prePrice = 0,

  emi = false,
  emiType = "on-demand",
  emiTenors = [],
  discount = false,

  active = false,
  onClick = () => { },
}: {
  price: number;
  prePrice?: number;

  emi?: boolean;
  emiType?: "on-demand" | "online" | "global" | "custom";
  emiTenors?: EMITenor[];
  discount?: boolean;

  active?: boolean;
  onClick?: () => void;
}) => {
  // console.log("product details page", { price, prePrice, emi, emiType, emiTenors, discount });
  const regularPrice = useMemo(
    () => (discount ? price : prePrice || price),
    [discount, price, prePrice],
  );
  const unitPrice =
    discount && prePrice > price ? (!emi ? prePrice : price) : undefined;

  const { minPercent, minPerMonth, maxMonth, regPrice } = useMemo(() => {
    let minPercent = 0;
    let minPerMonth = regularPrice;
    let minPrice = regularPrice;
    let regPrice = regularPrice;
    let maxMonth = 0;

    for (const tenor of emiTenors) {
      const month = Number(tenor.name.split(" ")[0]);
      const percent = emiType === "online" ? 0 : tenor.percent;
      const emiPrice = Math.ceil(
        regularPrice + (regularPrice * percent) / 100,
      );
      const perMonth = Math.ceil(emiPrice / month);

      if (minPrice > perMonth) {
        minPercent = percent;
        minPrice = perMonth;
      }
      if (minPerMonth > perMonth) {
        minPerMonth = perMonth;
      }
      if (maxMonth < month) {
        maxMonth = month;
        regPrice = emiPrice;
      }
    }

    return {
      minPercent,
      minPerMonth,
      regPrice,
      maxMonth,
    };
  }, [emiTenors, regularPrice, emiType]);

  const subtitle = useMemo(() => {
    if (emi) {
      const emiLabel =
        emiType === "online"
          ? "0%"
          : emiType === "on-demand"
            ? "On-Demand"
            : "";

      return `${emiLabel ? `${emiLabel} ` : ""}EMI Price: Tk. ${numberWithCommas(regPrice)}`;
    }

    return "Cash Discount Price";
  }, [emi, regPrice, emiType]);

  const info = useMemo(() => {
    if (emi) {
      return `Up to ${maxMonth} Months***`;
    }

    // return "Online / Cash Payment";
    return "";
  }, [emi, maxMonth]);

  return (
    <div
      className={`${classes.PaymentTab} ${active && classes.active}`}
      onClick={onClick}
    >
      <div className={classes.checkbox}>
        <CheckBox checked={active} />
      </div>

      <div className={classes.borderContents}>
        <div className={classes.text}>
          <p className={classes.price + " flex gap-2"}>
            {emi ? <span className={classes.info}>Start From</span> : "Tk. "}
            {!emi
              ? numberWithCommas(regularPrice)
              : `${numberWithCommas(minPerMonth)}/month`}
          </p>

          {/* {unitPrice && !emi && (
						<p className={classes.prePrice}>
							{numberWithCommas(unitPrice)}
						</p>
					)} */}
        </div>

        {subtitle && <p className={classes.subtitle}>{subtitle}</p>}

        {info && <p className={classes.info}>{info}</p>}
      </div>
    </div>
  );
};

export default Top;
